import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import LeftAlignment from "assets/img/svg/leftAlignment";
import classNames from "classnames";
import type { TImportTitles } from "components/ImportData/import_data";
import type { TImportDataDictionary } from "components/ImportData/type";
import type { IBeneficiar } from "containers/Beneficiars/types";
import type { ITitle } from "containers/Titles/types";
import { BodyHighlight } from "helpers/TypographyComponents";
import { convertAnyAsArray, formatLocaleDateString } from "helpers/utils";
import type { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import type { ITerm } from "store/managementStore";
import { useAppSelector } from "store/store";
import type { SubsetOf } from "types";

import type { TExampleData, TFileData } from "..";
import StepTitle from "../StepTitle";
import StepWrapper from "../StepWrapper";
import styles from "./style.module.scss";

interface IVerificationStep {
    activeStep: number;
    setActiveStep: Dispatch<SetStateAction<number>>;
    loadedData: TFileData | undefined;
    crealoKeys: TImportTitles | undefined;
    exampleData: TExampleData | undefined;
    /**
     * Record that saves the matching of their columns with our values.
     */
    selectedValues: Record<string, string>;
    /**
     * Keys (from our values) that can be float.
     */
    floatKeys: string[];
    element: TImportDataDictionary | undefined;
}

/**
 * Step that shows them changes between previous state and current state.
 */
const VerificationStep = ({
    activeStep,
    setActiveStep,
    loadedData,
    crealoKeys,
    exampleData,
    selectedValues,
    floatKeys,
    element,
}: IVerificationStep) => {
    const [t] = useTranslation();
    const columnNames = [
        t("BulkUpdates.VerificationStep.Column1"),
        t("BulkUpdates.VerificationStep.Column2"),
        t("BulkUpdates.VerificationStep.Column3"),
    ];
    const { customFields } = useAppSelector((state) => state.customFields);

    /**
     * Returns only keys matched in previous step.
     * But not "id" as there is no need to show them that.
     */
    const cleanedKeys = (): TImportTitles => {
        if (!loadedData || !crealoKeys) return {};
        const filteredKeys = { ...crealoKeys };
        Object.keys(crealoKeys).forEach((key) => {
            if (loadedData[0][key] === undefined) delete filteredKeys[key];
            delete filteredKeys["_id"];

            const matchExists = Object.keys(selectedValues).some((item) => selectedValues[item] === key);
            if (!matchExists) {
                delete filteredKeys[key];
            }
        });

        return filteredKeys;
    };

    const noChanges = !Object.keys(cleanedKeys()).length;

    /**
     * Gets value corresponding to key inside exampleData.
     */
    const getOldValue = (key) => {
        if (!exampleData || !loadedData) return;
        let value = exampleData[key];

        // Title otherInformation values:
        if ((exampleData as ITitle).otherInformation && (exampleData as ITitle).otherInformation[key])
            value = (exampleData as ITitle).otherInformation[key];

        // Title channel values:
        const isTitleChannelData = key.includes("channel") && (exampleData as ITitle).overriddenChannelsData;
        if (isTitleChannelData) {
            // Missing when no channel id column is mapped, a plain value when the file has a single one.
            const allChannelIds = convertAnyAsArray(loadedData[0]["_channelId"]);
            const cleanChannelKey = key.replace("channel", "").toLowerCase();
            const oldChannelData = (exampleData as ITitle).overriddenChannelsData; // Get data channels data from example.

            if (oldChannelData.length > 0) {
                // File ids are raw cell values (strings): match them against the title's channelId.
                value = allChannelIds.map((channelId) => {
                    const existingChannel = oldChannelData.find((item) => item.channelId === +channelId);
                    if (!existingChannel) return t("BulkUpdates.VerificationStep.NA");
                    // Channel prices are stored in cents, like the title price handled in formatOldValue
                    return cleanChannelKey === "price" ? existingChannel.price / 100 : existingChannel[cleanChannelKey];
                });
            } else {
                value = allChannelIds.map((channel) => t("BulkUpdates.VerificationStep.NA"));
            }
        }

        // Term channel values:
        const isTermChannelData = key.includes("channel") && (exampleData as ITerm).channelData;
        if (isTermChannelData) {
            // Missing when no channel id column is mapped, a plain value when the file has a single one.
            const allChannelIds = convertAnyAsArray(loadedData[0]["_channelId"]);
            const keyIsArray = Array.isArray(loadedData[0][`_${key}`]);

            // Get all channels from example term that match IDs selected.
            const channelInfo = (exampleData as ITerm).channelData.filter((channel) =>
                allChannelIds.includes(channel.channelId.toString()),
            );
            const oldChannelIds = channelInfo.map((item) => item.channelId); // All channelIds already present in term.
            if (key.includes("Id")) {
                // Show all channelIds
                if (keyIsArray) {
                    value = allChannelIds
                        .map((item) => (oldChannelIds.includes(+item) ? item : t("BulkUpdates.VerificationStep.NA")))
                        .join(", ");
                } else {
                    value = oldChannelIds.includes(+allChannelIds[0])
                        ? allChannelIds[0]
                        : t("BulkUpdates.VerificationStep.NA");
                }
            } else {
                // Create array with all channelsData..
                const channelData = channelInfo.map((info) => ({ channelId: info.channelId, data: info.data as any }));
                const milestoneIndex = +key.slice(-1) - 1;
                // Get all percentages values for milestone selected:
                if (key.includes("Percentage")) {
                    value = getOldChannelData({
                        allChannelIds,
                        oldChannelIds,
                        channelData,
                        milestoneIndex,
                        label: "percentage",
                    });
                }
                // Do same for milestone quantities:
                if (key.includes("Milestone")) {
                    value = getOldChannelData({
                        allChannelIds,
                        oldChannelIds,
                        channelData,
                        milestoneIndex,
                        label: "quantity",
                    });
                }
            }
        }

        if (key.includes("tags")) {
            if (Array.isArray(value)) {
                value = value.map((tag) => tag.name).join(", ");
            } else {
                value = value.name;
            }
        }

        if (key.includes("meta:")) {
            const fieldData = customFields.find((item) => item.uuid === key.replace("meta:", ""));
            const field = (exampleData as ITitle | IBeneficiar).customFields?.find(
                (item) => item.customFieldTypeId === fieldData?.id,
            );
            value = field?.value ?? "";

            if (fieldData?.valueType === "boolean" && field?.value) {
                value = +field.value ? t("CustomFields.CollapseInfo.No") : t("CustomFields.CollapseInfo.Yes");
            }
        }

        return value || t("BulkUpdates.VerificationStep.NA");
    };

    /**
     * Joins all old values of channels for one label (percentage or quantity).
     * If channel didn't exist before, the value is n.c.
     */
    const getOldChannelData = ({ allChannelIds, oldChannelIds, channelData, milestoneIndex, label }) => {
        const channelIds = convertAnyAsArray(allChannelIds);
        return channelIds.map((id) => {
            // If channelId already exists in contract:
            if (oldChannelIds.includes(+id)) {
                const channel = channelData.find((item) => +item.channelId === +id);
                return channel?.data[milestoneIndex]
                    ? channel?.data[milestoneIndex][label].toString().replace(",", ".")
                    : t("BulkUpdates.VerificationStep.NA");
            } else {
                // If it is a new channel:
                return t("BulkUpdates.VerificationStep.NA");
            }
        });
    };

    /**
     * Formats oldValue according to type.
     */
    const formatOldValue = (value: string, key: string) => {
        const valueIsArray = Array.isArray(value);

        // Handle date values:
        if (key.toLowerCase().includes("date"))
            return formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } });

        // Handle float keys:
        if (!valueIsArray && floatKeys.includes(key) && value !== t("BulkUpdates.VerificationStep.NA"))
            return (+value / 100).toString();

        // Handle repeated keys:
        if (valueIsArray) {
            if (floatKeys.includes(key)) return value.map((item) => item.toString().replace(",", ".")).join(", ");
            return value.join(", ");
        }

        if (typeof value === "number") {
            return (value as number).toString();
        }

        return value;
    };

    const exampleName: Record<
        SubsetOf<TImportDataDictionary, "titles" | "beneficiars" | "terms">,
        Record<string, string>
    > = {
        titles: {
            element: t("Titles.Title").toLowerCase(),
            title: `${(exampleData as ITitle | undefined)?.name ?? ""}`,
        },
        beneficiars: {
            element: t("Beneficiars.Beneficiar").toLowerCase(),
            title: `${(exampleData as IBeneficiar).name ?? ""} ${(exampleData as IBeneficiar).surname}`,
        },
        terms: {
            element: t("Terms.Term").toLowerCase(),
            title: `${(exampleData as ITerm).title} <> ${(exampleData as ITerm).author}`,
        },
    };

    const formatNewValue = ({ newValue, key, isArray }: { newValue: any; key: string; isArray: boolean }) => {
        let value = newValue;
        if (isArray) {
            value = newValue.map((value) => (!value ? "--" : value)).join(", ");
        } else if (key.includes("meta")) {
            const uuid = key.replace("_meta:", "");
            const currentField = customFields.find((item) => item.uuid === uuid);
            if (currentField?.valueType === "boolean")
                value = +newValue ? t("CustomFields.CollapseInfo.No") : t("CustomFields.CollapseInfo.Yes");
        }
        return value;
    };

    return (
        <StepWrapper activeStep={activeStep} setActiveStep={setActiveStep} conditionToDisable={noChanges}>
            <StepTitle
                title={t("BulkUpdates.VerificationStep.Title")}
                subtitle={t("BulkUpdates.VerificationStep.Subtitle")}
            />
            {noChanges && (
                <div className={styles["no-matches-info"]}>
                    <span>{t("BulkUpdates.VerificationStep.NoChanges1")}</span>
                    <span>{t("BulkUpdates.VerificationStep.NoChanges2")}</span>
                </div>
            )}
            {!noChanges && (
                <div>
                    <span className={styles["example-name"]}>
                        {t("BulkUpdates.VerificationStep.ExampleTitle", {
                            element: exampleName[element!].element,
                            name: exampleName[element!].title,
                        })}
                    </span>
                    <Paper className={styles["mapping-table-container"]}>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {columnNames.map((column, index) => {
                                            return (
                                                <TableCell className={styles["th-cell"]} key={`header-${index}`}>
                                                    <div>
                                                        <LeftAlignment />
                                                        <span>{column}</span>
                                                    </div>
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {Object.keys(cleanedKeys()).map((key, index) => {
                                        const columnTitle = crealoKeys ? crealoKeys[key] : "";
                                        const sanitisedKey = key.replace("_", "");
                                        const oldValue = formatOldValue(getOldValue(sanitisedKey), sanitisedKey);

                                        let newValue = (loadedData ?? [])[0][key];
                                        const newValueIsArray = Array.isArray((loadedData ?? [])[0][key]);

                                        newValue = formatNewValue({ newValue, key, isArray: newValueIsArray });

                                        return (
                                            <TableRow key={`titleKey-${index}`}>
                                                <TableCell className={styles["first-column"]}>{columnTitle}</TableCell>
                                                <TableCell
                                                    className={classNames({
                                                        [styles["old-value-column"]]: true,
                                                        [styles["not-strikethrough"]]: oldValue.includes(
                                                            t("BulkUpdates.VerificationStep.NA"),
                                                        ),
                                                    })}
                                                >
                                                    {oldValue}
                                                </TableCell>
                                                <TableCell className={styles["new-value-column"]} id={`data-${index}`}>
                                                    {loadedData ? newValue || "--" : "--"}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </div>
            )}
        </StepWrapper>
    );
};

export default VerificationStep;
